import { Textbox } from '@policygenius/mortar';
import classNames from 'classnames';
import Downshift from 'downshift';
import { createUseStyles } from 'react-jss';

import getErrorMessage from 'forms/getErrorMessage';

import styles from './styles';
import { TypeAheadFieldProps, TypeAheadFieldValues, TypeAheadItem } from './types';
import baseStyles from '../styles';
import useField from '../useField';

const useBaseStyles = createUseStyles(baseStyles);
const useStyles = createUseStyles(styles);

const TypeAheadField = <Value extends TypeAheadFieldValues>({
  id,
  name,
  menuItemsComponent: MenuItems,
  menuInitiallyOpen,
  config,
  ...props
}: TypeAheadFieldProps<Value>) => {
  const baseClasses = useBaseStyles();
  const classes = useStyles();
  const { input, meta } = useField<TypeAheadItem<Value>, HTMLInputElement, string>(name, config);

  const handleChange = (item: TypeAheadItem<Value> | null) => input.onChange(item ?? undefined);

  return (
    <Downshift<TypeAheadItem<Value>>
      onChange={handleChange}
      itemToString={(item) => `${item?.label ?? ''}`}
      initialSelectedItem={meta.initial ?? undefined}
      id={name}
      inputId={name}
      initialIsOpen={menuInitiallyOpen}
    >
      {({
        inputValue,
        isOpen,
        getInputProps,
        getMenuProps,
        getItemProps,
        selectedItem,
        highlightedIndex,
        clearSelection,
        toggleMenu,
      }) => (
        <div className={classNames(baseClasses.fieldContainer, classes.field)}>
          <Textbox
            {...input}
            {...props}
            {...getInputProps()}
            id={id ?? input.name}
            errorMessage={getErrorMessage(meta)}
            showLabel
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              if (!inputValue) {
                clearSelection();
              }

              return input.onBlur(e);
            }}
            onFocus={(e: React.FocusEvent<HTMLInputElement>) => {
              toggleMenu();

              return input.onFocus(e);
            }}
          />
          {isOpen ? (
            <ul {...getMenuProps()} className={classes.menu}>
              <MenuItems
                inputValue={inputValue}
                getItemProps={getItemProps}
                selectedItem={selectedItem}
                highlightedIndex={highlightedIndex}
              />
            </ul>
          ) : null}
        </div>
      )}
    </Downshift>
  );
};

export { default as getMenuItemsComponent } from './getMenuHelpers/getMenuItemsComponent';
export { default as getApolloMenuItemsComponent } from './getMenuHelpers/getApolloMenuItemsComponent';

export default TypeAheadField;
